import React, { useEffect, useRef, useState, useMemo } from "react";
import { Plus, Minus, Maximize, RotateCcw, RotateCw, Wrench, Sun, Moon, Star } from "lucide-react";
import { useCanvasGestures } from "../hooks/useCanvasGestures";
import { useFenceDraw } from "../hooks/useFenceDraw";
import { computeLabelLayout } from "../lib/labelLayout";
import FenceLayer from "./canvas/FenceLayer";
import FenceLabels from "./canvas/FenceLabels";

export default function Canvas({
  backgroundUrl, ppm, categories, elements, mode, tool,
  selectedId, onSelect, onElementClick, onPlace, onElementDrag, onElementCommit,
  calibPoints, onCalibPoint, onRubberBand, highlightIds, viewDrag,
  showLabelsOnly, showConsumption, showConsumptionNight = false, showMissingConsumption = false, showGrid = false, gridStep = 5, gridLocked = false, gridShiftX = 0, focusRequest,
  onFenceCommit, onFencePointsChange, onFencePointsCommit, canSeeDetails = true,
  guardMode = "day", onGuardPlace, labelLod = 26, showGuards = true, showFences = true,
  userDot = null, geoCalibPoints = null, onGeoCalibPoint,
}) {
  const vpRef = useRef(null);
  const tRef = useRef({ zoom: 1, panX: 0, panY: 0, rot: 0 });
  const tapRef = useRef(null);
  const [t, setT] = useState({ zoom: 1, panX: 0, panY: 0, rot: 0 });
  const [img, setImg] = useState({ w: 1600, h: 1000 });
  const [measured, setMeasured] = useState(false);
  const imgRef = useRef(img);
  const [band, setBand] = useState(null);
  const bandRef = useRef(null);
  const fitted = useRef(false);
  const drawingFence = mode === "admin" && (tool === "fence" || tool === "fence_small");

  const apply = (next) => { const m = { ...tRef.current, ...next }; tRef.current = m; setT(m); };
  useEffect(() => { imgRef.current = img; }, [img]);

  // refit when background changes (dimensions come from the visible <img> onLoad)
  useEffect(() => { fitted.current = false; setMeasured(false); }, [backgroundUrl]);

  const fit = () => {
    const vp = vpRef.current;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    const z = Math.min(r.width / img.w, r.height / img.h) * 0.92;
    apply({ zoom: z, panX: (r.width - img.w * z) / 2, panY: (r.height - img.h * z) / 2, rot: 0 });
  };

  useEffect(() => {
    if (fitted.current || !measured || !vpRef.current) return;
    fit();
    fitted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measured, img]);

  const s2w = (cx, cy) => {
    const r = vpRef.current.getBoundingClientRect();
    const tt = tRef.current;
    const dx = (cx - r.left - tt.panX) / tt.zoom;
    const dy = (cy - r.top - tt.panY) / tt.zoom;
    const a = -(tt.rot || 0) * Math.PI / 180;
    return { x: dx * Math.cos(a) - dy * Math.sin(a), y: dx * Math.sin(a) + dy * Math.cos(a) };
  };

  // world -> screen coordinates relative to the viewport top-left (inverse of s2w)
  const w2s = (wx, wy) => {
    const tt = tRef.current;
    const a = (tt.rot || 0) * Math.PI / 180;
    const rx = wx * Math.cos(a) - wy * Math.sin(a);
    const ry = wx * Math.sin(a) + wy * Math.cos(a);
    return { x: rx * tt.zoom + tt.panX, y: ry * tt.zoom + tt.panY };
  };

  // touch tap -> place a GPS calibration point (mobile: pointerdown returns early,
  // so single-finger taps are routed here from the gesture hook)
  useEffect(() => {
    if (tool === "geocalib" && onGeoCalibPoint) {
      tapRef.current = (cx, cy) => onGeoCalibPoint(s2w(cx, cy));
    } else {
      tapRef.current = null;
    }
  }, [tool, onGeoCalibPoint]);

  // rotate the whole view around the viewport center
  const rotateBy = (deg) => {
    const vp = vpRef.current; if (!vp) return;
    const r = vp.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const tt = tRef.current;
    const w = s2w(r.left + cx, r.top + cy);
    const nr = (tt.rot || 0) + deg;
    const a = nr * Math.PI / 180;
    const rx = w.x * Math.cos(a) - w.y * Math.sin(a);
    const ry = w.x * Math.sin(a) + w.y * Math.cos(a);
    apply({ rot: nr, panX: cx - tt.zoom * rx, panY: cy - tt.zoom * ry });
  };

  // reset rotation to north-up, keeping the current viewport center anchored
  const resetRotation = () => {
    const vp = vpRef.current; if (!vp) return;
    const r = vp.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const tt = tRef.current;
    const w = s2w(r.left + cx, r.top + cy);
    apply({ rot: 0, panX: cx - tt.zoom * w.x, panY: cy - tt.zoom * w.y });
  };

  // rotate buttons: single click = 1°, double click = 10° (disambiguated via a short timer)
  const rotClickRef = useRef({});
  const handleRotClick = (dir) => {
    const st = rotClickRef.current;
    if (st[dir]) {
      clearTimeout(st[dir]); st[dir] = null;
      rotateBy(dir * 10);
    } else {
      st[dir] = setTimeout(() => { st[dir] = null; rotateBy(dir * 1); }, 240);
    }
  };

  // center the viewport on a requested world point, accounting for a bottom inset
  // (e.g. the mobile details sheet covering the lower part of the screen)
  useEffect(() => {
    if (!focusRequest || !vpRef.current) return;
    const vp = vpRef.current;
    const r = vp.getBoundingClientRect();
    const inset = focusRequest.bottomInset || 0;

    // Fit a world-space bounding box (element + neighbours) into the viewport.
    if (focusRequest.bbox) {
      const b = focusRequest.bbox;
      const bw = Math.max(b.maxX - b.minX, 1);
      const bh = Math.max(b.maxY - b.minY, 1);
      const availW = Math.max(r.width, 1);
      const availH = Math.max(r.height - inset, 1);
      let z = Math.min(availW / bw, availH / bh);
      z = Math.min(8, Math.max(0.05, z));
      // keep the zoom that fits the cluster, but center on the SELECTED element
      const cx = b.cx != null ? b.cx : (b.minX + b.maxX) / 2;
      const cy = b.cy != null ? b.cy : (b.minY + b.maxY) / 2;
      apply({ zoom: z, rot: 0, panX: r.width / 2 - z * cx, panY: (r.height - inset) / 2 - z * cy });
      return;
    }

    const zoom = focusRequest.keepZoom ? tRef.current.zoom : Math.max(tRef.current.zoom, 1.5);
    const a = (tRef.current.rot || 0) * Math.PI / 180;
    const rx = focusRequest.x * Math.cos(a) - focusRequest.y * Math.sin(a);
    const ry = focusRequest.x * Math.sin(a) + focusRequest.y * Math.cos(a);
    apply({
      zoom,
      panX: r.width / 2 - zoom * rx,
      panY: (r.height - inset) / 2 - zoom * ry,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  // wheel zoom (non-passive)
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      e.preventDefault();
      const r = vp.getBoundingClientRect();
      const tt = tRef.current;
      const cx = e.clientX - r.left, cy = e.clientY - r.top;
      const wx = (cx - tt.panX) / tt.zoom, wy = (cy - tt.panY) / tt.zoom;
      const nz = Math.min(8, Math.max(0.05, tt.zoom * (1 - e.deltaY * 0.0015)));
      apply({ zoom: nz, panX: cx - wx * nz, panY: cy - wy * nz });
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, []);

  // touch gestures (Google-Maps style) extracted to a hook
  useCanvasGestures(vpRef, tRef, apply, tapRef);

  // in-progress fence drawing (draft points, cursor segment, finish/cancel)
  const { fenceDraft, fenceCursor, addPoint, trackCursor, finishFence } = useFenceDraw({
    drawingFence, s2w, onFenceCommit,
  });

  const startPan = (e) => {
    const start = { x: e.clientX, y: e.clientY };
    const base = { ...tRef.current };
    const move = (ev) =>
      apply({ zoom: base.zoom, panX: base.panX + (ev.clientX - start.x), panY: base.panY + (ev.clientY - start.y) });
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // rubber-band selection: the band is tracked and rendered in SCREEN space
  // (relative to the viewport), so it stays orthogonal to the screen even when
  // the map is rotated. Element hit-testing projects each element's world AABB
  // onto the screen and intersects it with the screen-space band.
  const startBand = (e) => {
    e.preventDefault();
    const r = vpRef.current.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    const move = (ev) => {
      const cx = ev.clientX - r.left, cy = ev.clientY - r.top;
      const rect = {
        x: Math.min(sx, cx), y: Math.min(sy, cy),
        w: Math.abs(cx - sx), h: Math.abs(cy - sy),
      };
      bandRef.current = rect;
      setBand(rect);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const rect = bandRef.current;
      if (rect && rect.w > 4 && rect.h > 4) {
        const ids = (elements || [])
          .filter((el) => el.kind !== "fence" && el.kind !== "guard")
          .filter((el) => {
            const hw = (el.length * ppm) / 2, hh = (el.width * ppm) / 2;
            // project the four world corners of the element to screen space
            const corners = [
              [el.x - hw, el.y - hh], [el.x + hw, el.y - hh],
              [el.x + hw, el.y + hh], [el.x - hw, el.y + hh],
            ].map(([wx, wy]) => w2s(wx, wy));
            const minX = Math.min(...corners.map((c) => c.x));
            const maxX = Math.max(...corners.map((c) => c.x));
            const minY = Math.min(...corners.map((c) => c.y));
            const maxY = Math.max(...corners.map((c) => c.y));
            // screen-space AABB overlap with the (orthogonal) band
            return maxX >= rect.x && minX <= rect.x + rect.w &&
                   maxY >= rect.y && minY <= rect.y + rect.h;
          })
          .map((el) => el.id);
        onRubberBand(ids);
      }
      bandRef.current = null;
      setBand(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onVpDown = (e) => {
    if (e.pointerType === "touch") return; // touch handled by gesture effect
    if (drawingFence) {
      if (e.button !== 0) return;
      addPoint(e.clientX, e.clientY);
      return;
    }
    if (e.target.dataset && e.target.dataset.role === "element") return;
    if (e.button === 1) { e.preventDefault(); startPan(e); return; }
    if (e.button !== 0) return;
    const world = s2w(e.clientX, e.clientY);
    if (mode === "admin" && tool === "place") { onPlace(world); return; }
    if (mode === "admin" && tool === "guard") { if (onGuardPlace) onGuardPlace(world); return; }
    if (mode === "admin" && tool === "calibrate") { onCalibPoint(world); return; }
    if (tool === "geocalib") { if (onGeoCalibPoint) onGeoCalibPoint(world); return; }
    if (mode === "admin") { onSelect(null); startPan(e); return; }
    // view mode -> pan or rubber-band depending on viewDrag
    if (viewDrag === "select") startBand(e);
    else startPan(e);
  };

  const onVpMove = (e) => {
    if (drawingFence) trackCursor(e.clientX, e.clientY);
  };

  const onVertexDown = (e, el, idx) => {
    if (e.pointerType === "touch") return;
    e.stopPropagation();
    const remap = (ev) => {
      const w = s2w(ev.clientX, ev.clientY);
      return el.points.map((p, i) => (i === idx ? { x: w.x, y: w.y } : p));
    };
    const move = (ev) => onFencePointsChange && onFencePointsChange(el.id, remap(ev));
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      onFencePointsCommit && onFencePointsCommit(el.id, remap(ev));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const clickGuard = useRef(false);
  const onElDown = (e, el) => {
    clickGuard.current = false;
    if (e.pointerType === "touch") return; // touch: select+zoom via onClick, pan/pinch via gesture effect
    e.stopPropagation();
    onSelect(el.id);
    const downX = e.clientX, downY = e.clientY;
    const isAdminDrag = mode === "admin" && tool === "select";
    const startW = s2w(e.clientX, e.clientY);
    const orig = { x: el.x, y: el.y };
    const move = (ev) => {
      if (!clickGuard.current && Math.hypot(ev.clientX - downX, ev.clientY - downY) > 4) clickGuard.current = true;
      if (isAdminDrag) {
        const cur = s2w(ev.clientX, ev.clientY);
        onElementDrag(el.id, orig.x + (cur.x - startW.x), orig.y + (cur.y - startW.y));
      }
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (isAdminDrag && clickGuard.current) {
        const cur = s2w(ev.clientX, ev.clientY);
        onElementCommit(el.id, orig.x + (cur.x - startW.x), orig.y + (cur.y - startW.y));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const zoomBy = (f) => {
    const vp = vpRef.current;
    const r = vp.getBoundingClientRect();
    const tt = tRef.current;
    const cx = r.width / 2, cy = r.height / 2;
    const wx = (cx - tt.panX) / tt.zoom, wy = (cy - tt.panY) / tt.zoom;
    const nz = Math.min(8, Math.max(0.05, tt.zoom * f));
    apply({ zoom: nz, panX: cx - wx * nz, panY: cy - wy * nz });
  };

  const onDblClick = (e) => {
    if (drawingFence) { finishFence(); return; }
    const r = vpRef.current.getBoundingClientRect();
    const cx = e.clientX - r.left, cy = e.clientY - r.top;
    const tt = tRef.current;
    const wx = (cx - tt.panX) / tt.zoom, wy = (cy - tt.panY) / tt.zoom;
    const nz = Math.min(8, Math.max(0.05, tt.zoom * 1.6));
    apply({ zoom: nz, panX: cx - wx * nz, panY: cy - wy * nz });
  };

  const labelElements = useMemo(
    () => showMissingConsumption
      ? elements.filter((el) => el.kind !== "fence" && el.kind !== "guard" && (Number(el.consumption) || 0) === 0)
      : elements,
    [elements, showMissingConsumption]
  );

  const labelLayout = useMemo(
    () => computeLabelLayout(labelElements, ppm, showConsumption, showConsumptionNight, showLabelsOnly, t.zoom, labelLod),
    [labelElements, ppm, showConsumption, showConsumptionNight, showLabelsOnly, t.zoom, labelLod]
  );

  let cursor = "grab";
  if (tool === "place" || tool === "calibrate" || tool === "guard" || tool === "geocalib" || drawingFence) {
    cursor = "crosshair";
  } else if (mode === "view" && viewDrag === "select") {
    cursor = "crosshair";
  }

  const MAP_FADE = 50; // px of gradual fade from the map into the neutral background

  return (
    <div
      ref={vpRef}
      data-testid="map-canvas-container"
      onPointerDown={onVpDown}
      onPointerMove={onVpMove}
      onDoubleClick={onDblClick}
      className="relative w-full h-full overflow-hidden"
      style={{ cursor, touchAction: "none", userSelect: "none", WebkitUserSelect: "none", backgroundColor: "#2A2E29" }}
    >
      <div
        style={{
          position: "absolute",
          transformOrigin: "0 0",
          transform: `translate(${t.panX}px, ${t.panY}px) scale(${t.zoom}) rotate(${t.rot || 0}deg)`,
        }}
      >
        {backgroundUrl && (
          <img
            src={backgroundUrl}
            alt="map"
            draggable={false}
            onLoad={(e) => { setImg({ w: e.target.naturalWidth, h: e.target.naturalHeight }); setMeasured(true); }}
            style={{
              width: measured ? img.w : "auto", height: measured ? img.h : "auto",
              maxWidth: "none", display: "block", pointerEvents: "none",
              WebkitMaskImage: `linear-gradient(to right, transparent 0, #000 ${MAP_FADE}px, #000 calc(100% - ${MAP_FADE}px), transparent 100%), linear-gradient(to bottom, transparent 0, #000 ${MAP_FADE}px, #000 calc(100% - ${MAP_FADE}px), transparent 100%)`,
              maskImage: `linear-gradient(to right, transparent 0, #000 ${MAP_FADE}px, #000 calc(100% - ${MAP_FADE}px), transparent 100%), linear-gradient(to bottom, transparent 0, #000 ${MAP_FADE}px, #000 calc(100% - ${MAP_FADE}px), transparent 100%)`,
              WebkitMaskComposite: "source-in",
              maskComposite: "intersect",
            }}
          />
        )}

        {/* reference grid (magenta) — admin & team only, between map and elements. gridStep in meters (5 or 1). Rotates with the map (skip when locked). gridShiftX shifts vertical lines horizontally (meters). */}
        {canSeeDetails && showGrid && !gridLocked && measured && (() => {
          const step = (gridStep || 5) * ppm;
          if (!(step > 0)) return null;
          // safety cap: avoid rendering tens of thousands of lines on huge maps
          if (img.w / step + img.h / step > 1200) return null;
          const sw = 1 / (t.zoom || 1);
          const dx = (gridShiftX || 0) * ppm;
          const vlines = [], hlines = [];
          for (let x = -step; x <= img.w + step + 0.5; x += step)
            vlines.push(<line key={`gv${x}`} x1={x} y1={0} x2={x} y2={img.h} stroke="#FF00FF" strokeWidth={sw} />);
          for (let y = 0; y <= img.h + 0.5; y += step)
            hlines.push(<line key={`gh${y}`} x1={0} y1={y} x2={img.w} y2={y} stroke="#FF00FF" strokeWidth={sw} />);
          return (
            <svg data-testid="map-grid-layer" width={img.w} height={img.h}
              style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}>
              <defs><clipPath id="grid-clip"><rect x={0} y={0} width={img.w} height={img.h} /></clipPath></defs>
              <g clipPath="url(#grid-clip)">
                <g transform={`translate(${dx},0)`}>{vlines}</g>
                {hlines}
              </g>
            </svg>
          );
        })()}

        {elements.filter((el) => el.kind !== "fence" && el.kind !== "guard" && (!showMissingConsumption || (Number(el.consumption) || 0) === 0)).map((el) => {
          const cat = categories.find((c) => c.id === el.category_id);
          const color = cat?.color || "#5E8A1E";
          const w = el.length * ppm;
          const h = el.width * ppm;
          const sel = selectedId === el.id;
          const hl = highlightIds && highlightIds.includes(el.id);
          const z = t.zoom || 1;
          const bw = 2 / z; // keep the outline ~2px on screen at any zoom (thin when zoomed in)
          let boxShadow = `inset 0 0 0 ${1.5 / z}px rgba(0,0,0,0.6)`;
          if (sel) boxShadow = `inset 0 0 0 ${2 / z}px #FFC300, inset 0 0 0 ${4 / z}px rgba(0,0,0,0.45)`;
          else if (hl) boxShadow = `inset 0 0 0 ${2 / z}px #5E8A1E, inset 0 0 0 ${4 / z}px rgba(0,0,0,0.35)`;
          return (
            <div
              key={el.id}
              data-role="element"
              data-testid={`map-element-${el.id}`}
              onPointerDown={(e) => onElDown(e, el)}
              onClick={(e) => {
                e.stopPropagation();
                if (clickGuard.current) { clickGuard.current = false; return; }
                onSelect(el.id);
                if (onElementClick) onElementClick(el.id);
              }}
              style={{
                position: "absolute",
                left: el.x,
                top: el.y,
                width: w,
                height: h,
                transform: `rotate(${el.rotation}deg)`,
                transformOrigin: "center",
                border: `${bw}px solid #FFFFFF`,
                background: showLabelsOnly ? "transparent" : color,
                boxShadow,
                boxSizing: "border-box",
                cursor: mode === "admin" ? "move" : "pointer",
                pointerEvents: drawingFence ? "none" : "auto",
              }}
            />
          );
        })}

        {/* fences (red polylines) + in-progress draft */}
        {!showMissingConsumption && showFences && (
        <FenceLayer
          elements={elements} selectedId={selectedId} mode={mode} tool={tool}
          zoom={t.zoom} drawingFence={drawingFence}
          fenceDraft={fenceDraft} fenceCursor={fenceCursor}
          onSelect={onSelect} onElementClick={onElementClick} onVertexDown={onVertexDown}
          draftColor={tool === "fence_small" ? "#F97316" : "#FF0000"}
        />
        )}

        {/* guard points (distinct shield markers; agent count follows guardMode) */}
        {!showMissingConsumption && showGuards && (() => { const z = t.zoom || 1; const GuardIcon = guardMode === "setup" ? Wrench : guardMode === "night" ? Moon : guardMode === "event" ? Star : Sun; return elements.filter((el) => el.kind === "guard" && (mode === "admin" || (Number(el[`agents_${guardMode}`]) || 0) > 0)).map((el) => {
          const sel = selectedId === el.id;
          const count = Number(el[`agents_${guardMode}`]) || 0;
          return (
            <div
              key={`guard-${el.id}`}
              data-role="element"
              data-testid={`map-element-${el.id}`}
              onPointerDown={(e) => onElDown(e, el)}
              onClick={(e) => {
                e.stopPropagation();
                if (clickGuard.current) { clickGuard.current = false; return; }
                onSelect(el.id);
                if (onElementClick) onElementClick(el.id);
              }}
              style={{
                position: "absolute", left: el.x, top: el.y,
                transform: `translate(-50%, -50%) scale(${1 / z}) rotate(${-(t.rot || 0)}deg)`,
                transformOrigin: "center",
                display: "flex", flexDirection: "column", alignItems: "center",
                cursor: mode === "admin" ? "move" : "pointer",
                pointerEvents: drawingFence ? "none" : "auto",
              }}
            >
              <div style={{
                width: 32, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
                background: "#0EA5E9", color: "#fff", borderRadius: "8px 8px 12px 12px",
                border: "2px solid #fff",
                boxShadow: sel ? "0 0 0 3px #FFC300, 0 1px 4px rgba(0,0,0,0.5)" : "0 1px 5px rgba(0,0,0,0.55)",
              }}>
                <GuardIcon size={17} strokeWidth={2.2} />
              </div>
              <div style={{
                marginTop: 2, background: "rgba(3,105,161,0.92)", color: "#fff",
                padding: "0px 6px", borderRadius: 3, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
              }} className="font-mono-data">
                {count} ag.
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: "#fff", whiteSpace: "nowrap",
                textShadow: "0 1px 2px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)",
              }}>
                {el.name}
              </div>
            </div>
          );
        }); })()}


        {/* element labels: LOD (name when large enough or selected, else a compact dot) + discreet style */}
        {(() => { const z = t.zoom || 1; return labelLayout.map((L) => {
          const sel = selectedId === L.id;
          const showName = L.named || sel;
          if (!showName) {
            const cat = categories.find((c) => c.id === L.categoryId);
            const dotColor = cat?.color || "#5E8A1E";
            return (
              <div
                key={`dot-${L.id}`}
                onClick={(e) => { e.stopPropagation(); onSelect(L.id); if (onElementClick) onElementClick(L.id); }}
                title={L.text}
                style={{
                  position: "absolute", left: L.anchorX, top: L.anchorY,
                  transform: `translate(-50%, -50%) scale(${1 / z})`, transformOrigin: "center",
                  width: 9, height: 9, borderRadius: "50%",
                  background: dotColor, border: "1.5px solid rgba(255,255,255,0.9)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  cursor: mode === "admin" ? "move" : "pointer",
                  pointerEvents: drawingFence ? "none" : "auto",
                }}
              />
            );
          }
          const moved = Math.hypot(L.x - L.anchorX, L.y - L.anchorY) > 2 / z;
          return (
            <React.Fragment key={`lbl-${L.id}`}>
              {moved && (
                <svg style={{ position: "absolute", left: 0, top: 0, overflow: "visible", pointerEvents: "none" }}>
                  <line x1={L.anchorX} y1={L.anchorY} x2={L.x} y2={L.y} stroke="#FFC300" strokeWidth={1 / z} opacity={0.7} />
                  <circle cx={L.anchorX} cy={L.anchorY} r={2 / z} fill="#FFC300" />
                </svg>
              )}
              <div
                style={{
                  position: "absolute", left: L.x, top: L.y,
                  transform: `translate(-50%, -50%) scale(${1 / z}) rotate(${-(t.rot || 0)}deg)`,
                  transformOrigin: "center",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  pointerEvents: "none", whiteSpace: "nowrap",
                  padding: "1px 5px", borderRadius: 4,
                  background: sel ? "rgba(0,47,167,0.82)" : "rgba(9,9,11,0.5)",
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 500, color: "#FFFFFF", lineHeight: 1.2, textShadow: "0 1px 1px rgba(0,0,0,0.7)" }}>
                  {L.text}
                </span>
                {L.showKw && (
                  <span className="font-mono-data" style={{ fontSize: 9, color: "#FFD84D", lineHeight: 1.2 }}>
                    {L.consumption} kW
                  </span>
                )}
                {L.showKwNight && (
                  <span className="font-mono-data" style={{ fontSize: 9, color: "#93C5FD", lineHeight: 1.2 }}>
                    ☾ {L.consumptionNight} kW
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        }); })()}

        {/* fence labels (name + length) at the polyline centroid */}
        {showFences && <FenceLabels elements={elements} ppm={ppm} zoom={t.zoom} rot={t.rot} canSeeDetails={canSeeDetails} />}

        {/* calibration markers */}
        {calibPoints.map((p) => (
          <div
            key={`calib-${p.x}-${p.y}`}
            style={{
              position: "absolute",
              left: p.x - 5,
              top: p.y - 5,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#E63946",
              border: "2px solid #fff",
            }}
          />
        ))}
        {calibPoints.length === 2 && (
          <svg style={{ position: "absolute", left: 0, top: 0, overflow: "visible", pointerEvents: "none" }}>
            <line
              x1={calibPoints[0].x} y1={calibPoints[0].y}
              x2={calibPoints[1].x} y2={calibPoints[1].y}
              stroke="#E63946" strokeWidth={2} strokeDasharray="4 4"
            />
          </svg>
        )}

        {/* GPS calibration markers (numbered 1..3) */}
        {geoCalibPoints && geoCalibPoints.map((p, i) => (
          <div key={`geocal-${i}`} data-testid={`geo-calib-marker-${i}`}
            style={{ position: "absolute", left: p.x, top: p.y, transform: `translate(-50%, -50%) scale(${1 / (t.zoom || 1)})`, pointerEvents: "none" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#7C3AED", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
              border: "2px solid #fff", boxShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{i + 1}</div>
          </div>
        ))}

        {/* live user location dot + accuracy circle */}
        {userDot && (
          <>
            {userDot.accuracyPx > 0 && (
              <div style={{ position: "absolute", left: userDot.x, top: userDot.y,
                width: userDot.accuracyPx * 2, height: userDot.accuracyPx * 2,
                transform: "translate(-50%, -50%)", borderRadius: "50%",
                background: "rgba(37,99,235,0.15)", border: "1px solid rgba(37,99,235,0.45)", pointerEvents: "none" }} />
            )}
            <div data-testid="user-location-dot"
              style={{ position: "absolute", left: userDot.x, top: userDot.y,
                transform: `translate(-50%, -50%) scale(${1 / (t.zoom || 1)})`, pointerEvents: "none" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#2563EB",
                border: "3px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.5)" }} />
            </div>
          </>
        )}
      </div>

      {/* rubber-band selection overlay — screen-fixed, stays orthogonal to the viewport even when the map is rotated */}
      {band && (
        <div
          data-testid="rubber-band-layer"
          style={{
            position: "absolute",
            left: band.x, top: band.y, width: band.w, height: band.h,
            border: "2px dashed #5E8A1E",
            background: "rgba(94,138,30,0.12)",
            pointerEvents: "none",
            zIndex: 6,
          }}
        />
      )}

      {/* locked reference grid — screen-fixed (doesn't rotate/pan with the map); spacing scales with zoom */}
      {canSeeDetails && showGrid && gridLocked && (() => {
        const sp = (gridStep || 5) * ppm * (t.zoom || 1);
        if (!(sp > 3)) return null;
        const dxScreen = (gridShiftX || 0) * ppm * (t.zoom || 1);
        const off = ((dxScreen % sp) + sp) % sp;
        const vp = vpRef.current;
        const W = vp ? vp.clientWidth : 0, H = vp ? vp.clientHeight : 0;
        const labels = [];
        if (W && H) {
          const per = Math.max(1, Math.ceil(48 / sp)); // group cells so labels stay ~>=48px apart
          const gap = per * sp;
          const meters = per * gridStep;
          const txt = `${meters} m`;
          const cols = Math.ceil(W / gap) + 1, rows = Math.ceil(H / gap) + 1;
          for (let i = 0; i < cols; i++)
            labels.push(
              <text key={`glx${i}`} x={i * gap + off + 4} y={13} fontSize="9" fontWeight="600"
                fill="#FF00FF" stroke="#fff" strokeWidth="2.5" paintOrder="stroke">{txt}</text>
            );
          for (let j = 1; j < rows; j++)
            labels.push(
              <text key={`gly${j}`} x={off + 4} y={j * gap + 13} fontSize="9" fontWeight="600"
                fill="#FF00FF" stroke="#fff" strokeWidth="2.5" paintOrder="stroke">{txt}</text>
            );
        }
        return (
          <svg data-testid="map-grid-locked" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 5 }}>
            <defs>
              <pattern id="locked-grid-pat" x={off} y={0} width={sp} height={sp} patternUnits="userSpaceOnUse">
                <path d={`M ${sp} 0 L 0 0 0 ${sp}`} fill="none" stroke="#FF00FF" strokeWidth="1" opacity="0.85" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#locked-grid-pat)" />
            {labels}
          </svg>
        );
      })()}

      {/* North indicator / compass — tap to reset rotation to north-up */}
      <button data-testid="compass-reset-btn" onClick={resetRotation} title="Orientează spre nord"
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-[#A8C97D] shadow flex items-center justify-center hover:border-[#E63946] transition-colors">
        <div className="relative w-7 h-7" style={{ transform: `rotate(${t.rot || 0}deg)`, transition: "transform 0.12s ease-out" }}>
          <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 0, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: "11px solid #E63946" }} />
          <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: 0, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "11px solid #A1A1AA" }} />
          <span className="absolute left-1/2 -translate-x-1/2 font-bold text-white" style={{ top: "1px", fontSize: "8px", lineHeight: 1 }}>N</span>
        </div>
      </button>

      {/* zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-1 z-10">
        <button data-testid="zoom-in-btn" onClick={() => zoomBy(1.2)}
          className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white transition-colors">
          <Plus size={16} strokeWidth={1.5} />
        </button>
        <button data-testid="zoom-out-btn" onClick={() => zoomBy(1 / 1.2)}
          className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white transition-colors">
          <Minus size={16} strokeWidth={1.5} />
        </button>
        <button data-testid="zoom-fit-btn" onClick={fit}
          className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white transition-colors">
          <Maximize size={16} strokeWidth={1.5} />
        </button>
        <button data-testid="rotate-left-btn" onClick={() => handleRotClick(-1)} onDoubleClick={(e) => e.stopPropagation()} title="Rotește stânga (click 1°, dublu-click 10°)"
          className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white transition-colors">
          <RotateCcw size={16} strokeWidth={1.5} />
        </button>
        <button data-testid="rotate-right-btn" onClick={() => handleRotClick(1)} onDoubleClick={(e) => e.stopPropagation()} title="Rotește dreapta (click 1°, dublu-click 10°)"
          className="w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white transition-colors">
          <RotateCw size={16} strokeWidth={1.5} />
        </button>
      </div>

      {(tool === "calibrate" || tool === "place" || tool === "geocalib") && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-[#09090B] text-white text-sm px-4 py-2 font-mono-data">
          {tool === "calibrate"
            ? `Click 2 puncte pe hartă pentru a defini scara (${calibPoints.length}/2)`
            : tool === "geocalib"
            ? `Atinge pe hartă locul unde te afli (${(geoCalibPoints || []).length}/3)`
            : "Click pe hartă pentru a amplasa elementul"}
        </div>
      )}
    </div>
  );
}
